<svelte:options
  customElement={{
    tag: "oc-paper-card-v1",
    extend: window.__components.extend(),
    props: {
      asButton: { type: "Boolean", attribute: "as-button" },
      backgroundColor: { type: "String", attribute: "background-color" },
      base64Href: { type: "String", attribute: "base64-href" },
      elevationLevel: { type: "String", attribute: "elevation-level" },
      rel: { type: "String" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      size: { type: "String" },
      target: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import type { Props } from "./PaperCardV1.types.js";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import { InteractiveElement } from "../../../common/components/InteractiveElement";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    size = "100",
    backgroundColor = undefined,
    elevationLevel = "canvas",
    asButton = false,
    base64Href,
    ocAriaLabel,
    rel,
    target,
  }: Props = $props();

  let Host = $host();

  const slots = useSlots(Host);

  let orientation: "horizontal" | "vertical" = $derived(size === "100" ? "horizontal" : "vertical");

  let hover = $state(false);
  let active = $state(false);

  /*                                                                                                       */
  const ontransitionstart = (event: TransitionEvent): void => {
    const eventTarget = event.composedPath()[0] as HTMLElement;
    active = getComputedStyle(eventTarget).boxShadow !== "none";
  };
</script>

<InteractiveElement
  {Host}
  {asButton}
  {base64Href}
  {ocAriaLabel}
  {rel}
  {target}
  {elevationLevel}
  class={`--elevation-${elevationLevel} ${hover ? "--hover" : ""} ${active ? "--active" : ""}`}
  style={`${backgroundColor ? `--background-color: ${backgroundColor};` : ""}`}
  onmouseover={() => (hover = true)}
  onmouseleave={() => (hover = false)}
  {ontransitionstart}
>
  {#snippet children(onslotchange)}
    <div class={`paper-card paper-card--size-${size}`}>
      <slot name="primary-action" {onslotchange} />
      <slot class={`paper-card__content ${slots.footer ? "" : "--without-footer"}`} />
      {#if slots.footer}
        <div class="paper-card__divider">
          <oc-divider-v1 variant="dashed" {orientation}></oc-divider-v1>
        </div>
        <slot class="paper-card__footer" name="footer" />
      {/if}
    </div>
  {/snippet}
</InteractiveElement>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../floating-focus/v1/FloatingFocusV1.shared" as floating-focus;
  :global {
    $fixture: 1px; /*                                                                                                                                              */

    :host {
      position: relative;
      width: fit-content;

      /*        */
      --background-color: initial;
    }

    .paper-card {
      all: unset;
      display: flex;

      --cut-out-smoothness: 0px; /*                          */

      --cut-out-radius: calc(#{tokens.$oc-component-paper-card-cutout-size} / 2);
      --cut-out-smooth: calc(var(--cut-out-radius) - var(--cut-out-smoothness));

      --left-cut: radial-gradient(
          circle at 0,
          #0000 var(--cut-out-smooth),
          #000 var(--cut-out-radius)
        )
        no-repeat left center / calc(var(--cut-out-radius) + #{$fixture}) 100%;

      --middle-fill: linear-gradient(to right, #000 0, #000 100%) no-repeat
        calc(var(--cut-out-radius)) center / calc(100% - 2 * var(--cut-out-radius)) 100%;

      --right-cut: radial-gradient(
          circle at 100%,
          #0000 var(--cut-out-smooth),
          #000 var(--cut-out-radius)
        )
        no-repeat right center / calc(var(--cut-out-radius) + #{$fixture}) 100%;
    }

    /*               */
    .--elevation-canvas {
      --bg-elevation: var(--background-color, #{tokens.$oc-semantic-color-canvas-background});
      filter: tokens.$oc-semantic-shadow-canvas;
    }

    .--elevation-stacked {
      --bg-elevation: var(--background-color, #{tokens.$oc-semantic-color-stacked-background});
      filter: tokens.$oc-semantic-shadow-stacked;
    }

    .--elevation-above {
      --bg-elevation: var(--background-color, #{tokens.$oc-semantic-color-above-background});
      filter: tokens.$oc-semantic-shadow-above;
    }

    .--elevation-sticky {
      --bg-elevation: var(--background-color, #{tokens.$oc-semantic-color-sticky-background});
      filter: tokens.$oc-semantic-shadow-sticky;
    }

    .paper-card__content,
    .paper-card__footer,
    .paper-card__divider {
      display: block;
      background: var(--bg-elevation);
    }

    .paper-card--size-50 {
      .paper-card__content {
        flex: 1 0 0;
        min-width: 0;
        padding: tokens.$oc-component-paper-card-50-spacing-y 0
          tokens.$oc-component-paper-card-50-spacing-y tokens.$oc-component-paper-card-50-spacing-x;
        border-radius: tokens.$oc-component-paper-card-border-radius 0 0
          tokens.$oc-component-paper-card-border-radius;

        &.--without-footer {
          padding-right: tokens.$oc-component-paper-card-50-spacing-x;
          border-radius: tokens.$oc-component-paper-card-border-radius;
        }
      }

      .paper-card__footer {
        flex: 0 1 auto;

        display: flex;
        align-items: center;

        padding: tokens.$oc-component-paper-card-50-spacing-y
          tokens.$oc-component-paper-card-50-spacing-x tokens.$oc-component-paper-card-50-spacing-y
          0;
        border-radius: 0 tokens.$oc-component-paper-card-border-radius
          tokens.$oc-component-paper-card-border-radius 0;
      }

      .paper-card__divider {
        padding: tokens.$oc-component-paper-card-50-spacing-y
          calc(#{tokens.$oc-component-paper-card-divider-area-width} / 2 + $fixture);

        margin: 0 -1 * $fixture;

        > oc-divider-v1 {
          height: 100%;
        }
      }
    }

    .paper-card--size-100 {
      flex-direction: column;

      .paper-card__content {
        padding: tokens.$oc-component-paper-card-100-spacing
          tokens.$oc-component-paper-card-100-spacing tokens.$oc-component-paper-card-100-gap-y
          tokens.$oc-component-paper-card-100-spacing;
        border-radius: tokens.$oc-component-paper-card-border-radius
          tokens.$oc-component-paper-card-border-radius 0 0;
      }

      .paper-card__footer {
        padding: tokens.$oc-component-paper-card-100-gap-y
          tokens.$oc-component-paper-card-100-spacing tokens.$oc-component-paper-card-100-spacing
          tokens.$oc-component-paper-card-100-spacing;

        border-radius: 0 0 tokens.$oc-component-paper-card-border-radius
          tokens.$oc-component-paper-card-border-radius;
      }

      .paper-card__divider {
        padding: calc(#{tokens.$oc-component-paper-card-divider-area-height} / 2 + $fixture)
          tokens.$oc-semantic-spacing-100;
        margin: -1 * $fixture 0;
        width: 100%;

        > oc-divider-v1 {
          width: 100%;
        }
      }
    }

    /*             */
    .paper-card--size-50,
    .paper-card--size-100 .paper-card__divider {
      mask: var(--left-cut), var(--middle-fill), var(--right-cut);
    }

    /*           */
    .paper-card {
      /*      */
      @include mixins.max-dpr(2.99) {
        --cut-out-smoothness: 1px; /*                                  */
      }
    }

    .-light-dom-interactive {
      pointer-events: none; /*                                     */
    }

    /*                    */
    ::slotted([slot="primary-action"]) {
      all: unset;
      position: absolute;
      display: block;
      inset: 0;
      pointer-events: auto;
      cursor: pointer;

      @include floating-focus.full-bleed();
      --oc-floating-focus-v1-radius: #{tokens.$oc-component-paper-card-border-radius};
    }

    /*                                           */
    .-interactive {
      all: unset;

      --oc-floating-focus-v1-radius: #{tokens.$oc-component-paper-card-border-radius};

      &:before {
        content: "";
        position: absolute;
        inset: 0;
      }
    }

    /*                  */
    .-interactive,
    .-light-dom-interactive {
      --interactive-border-radius: calc(
        #{tokens.$oc-component-paper-card-border-radius} +
          #{tokens.$oc-component-interactive-overlay-default-offset}
      );
      --interactive-inset: calc(-1 * #{tokens.$oc-component-interactive-overlay-default-offset});
      &.--hover {
        cursor: pointer;

        &:after {
          content: "";
          position: absolute;
          inset: var(--interactive-inset);
          border-radius: var(--interactive-border-radius);
          background-color: tokens.$oc-component-interactive-overlay-background-color-hover;
          pointer-events: none;
        }
      }
    }

    /*                  */
    .-interactive:active,
    .-light-dom-interactive.--active {
      &:after {
        content: "";
        position: absolute;
        inset: var(--interactive-inset);
        border-radius: var(--interactive-border-radius);
        background-color: tokens.$oc-component-interactive-overlay-background-color-active;
        pointer-events: none;
      }
    }

    /*                                            */
    /*                                         */
    ::slotted([slot="primary-action"]) {
      transition: box-shadow 1ms;
    }
    ::slotted([slot="primary-action"]:active) {
      box-shadow: 0 0 0 0 #0000;
    }
  }
</style>
